import { useEffect, useRef, useState } from 'react'
import { Eye, FileText, Loader2, Trash2, Upload, X } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'
import {
  fileKindLabel,
  isImageValue,
  type UploadedFileItem,
} from '@/shared/lib/file-types'

export type { UploadedFileItem }

/**
 * The one shared file uploader. A drag/click dropzone with a centered label
 * and an acceptable-types subtitle, plus a per-file list: each row shows a
 * spinner while uploading, then a thumbnail (image) or doc icon, the file
 * name, a type caption, and View / Remove actions (Cancel while in-flight).
 *
 * It owns the upload orchestration: give it `uploadFn` and it manages the
 * in-flight state, appends finished files to `value`, and lets the user
 * cancel or remove. Works in single-file (`multiple={false}`) and multi-file
 * modes. Both the generated `FileField` (react-hook-form) and `MetadataField`
 * (prop-driven) adapt to this component, as do bespoke upload surfaces.
 */

interface FileUploaderProps {
  /** Committed, already-uploaded files. */
  value: UploadedFileItem[]
  /** Called with the new committed list whenever it changes. */
  onChange: (files: UploadedFileItem[]) => void
  /** Upload transport: resolve to the stored file, throw on failure. */
  uploadFn: (file: File) => Promise<UploadedFileItem>
  /** Allow more than one file. Single mode replaces on a new pick. */
  multiple?: boolean
  /** HTML `accept` for the picker. */
  accept?: string
  /** Human label of accepted types shown under the dropzone (e.g. "PNG, JPG, PDF"). */
  acceptLabel?: string
  /** Overrides the main dropzone label ("Drop or click to upload"). */
  hint?: string
  disabled?: boolean
  error?: boolean
  /** Open/preview a committed file. Defaults to opening `file_url` in a tab. */
  onOpenFile?: (file: UploadedFileItem) => void
  /** Show the remove (trash) action on committed rows. Default true. Set
   *  false where a committed file is already persisted and shouldn't be
   *  deleted from this surface (e.g. an append-only "add" modal). */
  canRemove?: boolean
  /** Custom remove handler for committed rows. When provided, clicking the
   *  trash calls this instead of dropping the row from the list locally - the
   *  caller owns the removal (e.g. a confirm dialog + server delete) and
   *  reflects it back through `onChange`. Omit for the default local remove. */
  onRemove?: (file: UploadedFileItem, index: number) => void
  className?: string
}

/** A file being uploaded (or that failed), tracked locally beside `value`. */
type PendingUpload = {
  id: number
  name: string
  mime: string
  status: 'uploading' | 'error'
}

let uploadIdSeq = 0
const nextUploadId = () => (uploadIdSeq += 1)

export function FileUploader({
  value,
  onChange,
  uploadFn,
  multiple = false,
  accept,
  acceptLabel,
  hint,
  disabled = false,
  error = false,
  onOpenFile,
  canRemove = true,
  onRemove,
  className,
}: FileUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragActive, setDragActive] = useState(false)
  const [pending, setPending] = useState<PendingUpload[]>([])

  // Latest committed value, read inside async upload callbacks so concurrent
  // uploads append correctly. Kept in sync after each render, and written
  // synchronously on commit/remove so back-to-back resolves see each other.
  const valueRef = useRef(value)
  useEffect(() => {
    valueRef.current = value
  }, [value])
  const cancelledRef = useRef<Set<number>>(new Set())

  const commit = (item: UploadedFileItem) => {
    const next = multiple ? [...valueRef.current, item] : [item]
    valueRef.current = next
    onChange(next)
  }

  const startUpload = (file: File) => {
    const id = nextUploadId()
    setPending((prev) => [
      ...prev,
      { id, name: file.name, mime: file.type, status: 'uploading' },
    ])
    uploadFn(file)
      .then((result) => {
        if (cancelledRef.current.has(id)) {
          cancelledRef.current.delete(id)
          return
        }
        commit(result)
        setPending((prev) => prev.filter((p) => p.id !== id))
      })
      .catch(() => {
        if (cancelledRef.current.has(id)) {
          cancelledRef.current.delete(id)
          return
        }
        // The caller's uploadFn is expected to surface the error (toast); we
        // keep a failed row so the user can remove and retry rather than
        // silently dropping it.
        setPending((prev) =>
          prev.map((p) => (p.id === id ? { ...p, status: 'error' } : p)),
        )
      })
  }

  const acceptFiles = (files: File[]) => {
    if (disabled || files.length === 0) return
    if (!multiple) {
      // Replace: abandon any in-flight upload and the current committed file.
      pending.forEach((p) => cancelledRef.current.add(p.id))
      setPending([])
      valueRef.current = []
      onChange([])
      startUpload(files[0])
      return
    }
    files.forEach(startUpload)
  }

  const removeCommitted = (index: number) => {
    const next = value.filter((_, i) => i !== index)
    valueRef.current = next
    onChange(next)
  }

  const cancelPending = (id: number) => {
    cancelledRef.current.add(id)
    setPending((prev) => prev.filter((p) => p.id !== id))
  }

  const openFile = (file: UploadedFileItem) => {
    if (onOpenFile) return onOpenFile(file)
    if (file.file_url) window.open(file.file_url, '_blank', 'noopener,noreferrer')
  }

  const hasAny = value.length > 0 || pending.length > 0
  const showDropzone = multiple || !hasAny

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {showDropzone && (
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'h-auto w-full flex-col justify-center gap-1 border-dashed py-6 transition-colors',
            dragActive
              ? 'border-[var(--green)] bg-[var(--green-bg)] text-[var(--green)]'
              : error
                ? 'border-[var(--status-error)] text-[var(--ink-3)]'
                : 'text-[var(--ink-3)]',
          )}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault()
            if (!dragActive && !disabled) setDragActive(true)
          }}
          onDragLeave={(e) => {
            e.preventDefault()
            if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
            setDragActive(false)
          }}
          onDrop={(e) => {
            e.preventDefault()
            setDragActive(false)
            const dropped = Array.from(e.dataTransfer.files ?? [])
            if (dropped.length) acceptFiles(dropped)
          }}
        >
          <Upload className="h-5 w-5 opacity-60" />
          <span className="whitespace-normal text-center text-[13px] font-medium text-[var(--ink-2)]">
            {dragActive
              ? multiple
                ? 'Drop files to upload'
                : 'Drop file to upload'
              : hint || 'Drop or click to upload'}
          </span>
          {acceptLabel && (
            <span className="max-w-full whitespace-normal break-words px-4 text-center text-[11px] font-normal normal-case leading-snug text-[var(--ink-3)]">
              {acceptLabel}
              {multiple ? ' - single or multiple' : ''}
            </span>
          )}
        </Button>
      )}

      {hasAny && (
        <ul className="flex flex-col gap-1.5">
          {value.map((item, index) => {
            const image = isImageValue(item.mime_type, item.file_name)
            return (
              <li
                key={item.uuid || `committed-${index}`}
                className="flex items-center gap-3 rounded-[8px] border border-input bg-[var(--bg-subtle)] p-2.5"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-[6px] border border-input bg-[var(--bg)]">
                  {image && item.file_url ? (
                    <img src={item.file_url} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <FileText className="h-4 w-4 text-[var(--ink-3)]" />
                  )}
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-[13px] text-[var(--ink)]">
                    {item.file_name}
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.06em] text-[var(--ink-3)]">
                    {fileKindLabel(item.mime_type, item.file_name)}
                  </span>
                </span>
                {item.file_url && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`View ${item.file_name}`}
                    onClick={() => openFile(item)}
                  >
                    <Eye className="h-3.5 w-3.5 text-[var(--ink-3)]" />
                  </Button>
                )}
                {canRemove && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    disabled={disabled}
                    aria-label={`Remove ${item.file_name}`}
                    onClick={() =>
                      onRemove ? onRemove(item, index) : removeCommitted(index)
                    }
                  >
                    <Trash2 className="h-3.5 w-3.5 text-[var(--status-error)]" />
                  </Button>
                )}
              </li>
            )
          })}
          {pending.map((p) => (
            <li
              key={`pending-${p.id}`}
              className="flex items-center gap-3 rounded-[8px] border border-input bg-[var(--bg-subtle)] p-2.5"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[6px] border border-input bg-[var(--bg)]">
                {p.status === 'error' ? (
                  <X className="h-4 w-4 text-[var(--status-error)]" />
                ) : (
                  <Loader2 className="h-4 w-4 animate-spin text-[var(--green)]" />
                )}
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-[13px] text-[var(--ink)]">{p.name}</span>
                <span className="text-[11px] uppercase tracking-[0.06em] text-[var(--ink-3)]">
                  {p.status === 'error'
                    ? 'Upload failed'
                    : `${fileKindLabel(p.mime, p.name)} - uploading...`}
                </span>
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label={`Cancel ${p.name}`}
                onClick={() => cancelPending(p.id)}
              >
                <X className="h-3.5 w-3.5 text-[var(--ink-3)]" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        className="hidden"
        onChange={(event) => {
          const files = Array.from(event.target.files ?? [])
          acceptFiles(files)
          event.currentTarget.value = ''
        }}
      />
    </div>
  )
}
